import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Input, Select, Table, Tag, Statistic, Row, Col, Typography,
  Alert, Popconfirm, Tabs, Slider, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  FireOutlined, SaveOutlined, DeleteOutlined, BarChartOutlined,
  DashboardOutlined, CheckCircleOutlined, HistoryOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { calculateBoilerECM, BoilerECMInputs } from '../../utils/boilerECMCalc';
import { saveBoilerECMResult, getBoilerECMResults, deleteBoilerECMResult } from '../../firebase/firestore';
import { BoilerECMResult } from '../../models/types';

interface Props { projectId: string; }

const FUEL_TYPES = ['Natural Gas', 'Fuel Oil', 'Coal', 'Dual-NG/Oil', 'Electric'] as const;
const SERVICE_TYPES = ['Steam', 'Hot Water'] as const;
const FLUE_TYPES = ['Condensing', 'Non-Condensing'] as const;
const CONSTRUCTION_TYPES = ['Water Tube', 'Fire Tube'] as const;
const ECM1_OPTIONS = Array.from({ length: 25 }, (_, i) => i);
const ECM2_OPTIONS = Array.from({ length: 31 }, (_, i) => i);
const ECM3_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 50];
const ECM4_OPTIONS = Array.from({ length: 366 }, (_, i) => i);

const fmt$ = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtDec = (n: number, d = 2) => n.toFixed(d);

const toOption = (v: string) => ({ value: v, label: v });

const defaultInputs = (): BoilerECMInputs => ({
  nameplateInputMmbtuh: 10,
  nameplateOutputMmbtuh: 8.5,
  operatingDaysPerYear: 365,
  operatingHoursPerDay: 24,
  boilerEfficiencyPct: 80,
  fuelCostPerTherm: 1.0,
  minConnectedLoadMmbtuh: 2,
  maxConnectedLoadMmbtuh: 8,
  variableFiringRatePct: 50,
  ecm1ReducedHoursPerDay: 0,
  ecm2ImprovedEfficiencyPct: 0,
  ecm3LoadReductionPct: 0,
  ecm4ReducedDaysPerYear: 0,
});

const defaultMeta = () => ({
  label: '',
  idNumber: '',
  location: '',
  manufacturer: '',
  modelNumber: '',
  serialNumber: '',
  nationalBoardNumber: '',
  yearBuilt: '',
  fuelType: 'Natural Gas' as BoilerECMResult['fuelType'],
  serviceType: 'Hot Water' as BoilerECMResult['serviceType'],
  flueType: 'Non-Condensing' as BoilerECMResult['flueType'],
  constructionType: 'Fire Tube' as BoilerECMResult['constructionType'],
  notes: '',
});

const activeEcmsOf = (r: BoilerECMResult): string => [
  r.ecm1ReducedHoursPerDay > 0 && '#1',
  r.ecm2ImprovedEfficiencyPct > 0 && '#2',
  r.ecm3LoadReductionPct > 0 && '#3',
  r.ecm4ReducedDaysPerYear > 0 && '#4',
].filter(Boolean).join(', ');

interface FiringRow { key: string; rate: string; annual: number; extra: number; selected?: boolean; }

const BoilerECMModule: React.FC<Props> = ({ projectId }) => {
  const { token } = theme.useToken();
  const [inputs, setInputs] = useState<BoilerECMInputs>(defaultInputs());
  const [meta, setMeta] = useState(defaultMeta());
  const [activeTab, setActiveTab] = useState<'design' | 'operating' | 'ecm'>('design');
  const [results, setResults] = useState<BoilerECMResult[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const calc = calculateBoilerECM(inputs);

  const loadResults = useCallback(() => {
    getBoilerECMResults(projectId)
      .then(setResults)
      .catch((err: unknown) => {
        const e = err as { message?: string; code?: string; name?: string };
        console.error('[BoilerECM] load error:', JSON.stringify({ code: e?.code, message: e?.message, name: e?.name, projectId }));
        setError(`Failed to load saved results: ${e?.code ?? ''} ${e?.message ?? String(err)} (projectId: ${projectId})`);
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const setNum = (field: keyof BoilerECMInputs) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setInputs(prev => ({ ...prev, [field]: parseFloat(e.target.value) || 0 }));
  };

  /* Value-based variants for AntD Select / Slider (they pass the value, not an event). */
  const setNumVal = (field: keyof BoilerECMInputs) => (v: number) => {
    setInputs(prev => ({ ...prev, [field]: v || 0 }));
  };

  const setMt = (field: keyof typeof meta) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setMeta(prev => ({ ...prev, [field]: e.target.value }));
  };

  const setMtVal = (field: keyof typeof meta) => (v: string) => {
    setMeta(prev => ({ ...prev, [field]: v }));
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const result: Omit<BoilerECMResult, 'id'> = {
        projectId,
        label: meta.label,
        idNumber: meta.idNumber,
        location: meta.location,
        manufacturer: meta.manufacturer,
        modelNumber: meta.modelNumber,
        serialNumber: meta.serialNumber,
        nationalBoardNumber: meta.nationalBoardNumber,
        yearBuilt: meta.yearBuilt,
        fuelType: meta.fuelType,
        serviceType: meta.serviceType,
        flueType: meta.flueType,
        constructionType: meta.constructionType,
        nameplateInputMmbtuh: inputs.nameplateInputMmbtuh,
        nameplateOutputMmbtuh: inputs.nameplateOutputMmbtuh,
        calculatedMaxEfficiencyPct: calc.calculatedMaxEfficiencyPct,
        calculatedHorsepower: calc.calculatedHorsepower,
        operatingDaysPerYear: inputs.operatingDaysPerYear,
        operatingHoursPerDay: inputs.operatingHoursPerDay,
        boilerEfficiencyPct: inputs.boilerEfficiencyPct,
        fuelCostPerTherm: inputs.fuelCostPerTherm,
        minConnectedLoadMmbtuh: inputs.minConnectedLoadMmbtuh,
        maxConnectedLoadMmbtuh: inputs.maxConnectedLoadMmbtuh,
        variableFiringRatePct: inputs.variableFiringRatePct,
        ecm1ReducedHoursPerDay: inputs.ecm1ReducedHoursPerDay,
        ecm2ImprovedEfficiencyPct: inputs.ecm2ImprovedEfficiencyPct,
        ecm3LoadReductionPct: inputs.ecm3LoadReductionPct,
        ecm4ReducedDaysPerYear: inputs.ecm4ReducedDaysPerYear,
        runTimeHours: calc.runTimeHours,
        annualCostAtMin: calc.annualCostAtMin,
        annualCostAtMax: calc.annualCostAtMax,
        annualCostAtSelected: calc.annualCostAtSelected,
        hourlyCostAtMin: calc.hourlyCostAtMin,
        hourlyCostAtMax: calc.hourlyCostAtMax,
        hourlyCostAtSelected: calc.hourlyCostAtSelected,
        ecmOperatingDays: calc.ecmOperatingDays,
        ecmOperatingHoursPerDay: calc.ecmOperatingHoursPerDay,
        ecmEfficiencyPct: calc.ecmEfficiencyPct,
        ecmRunTimeHours: calc.ecmRunTimeHours,
        ecmAnnualCostAtMin: calc.ecmAnnualCostAtMin,
        ecmAnnualCostAtMax: calc.ecmAnnualCostAtMax,
        ecmAnnualCostAtSelected: calc.ecmAnnualCostAtSelected,
        ecmHourlyCostAtSelected: calc.ecmHourlyCostAtSelected,
        savingsAtMin: calc.savingsAtMin,
        savingsAtMax: calc.savingsAtMax,
        savingsMidRange: calc.savingsMidRange,
        savingsPctAtMax: calc.savingsPctAtMax,
        notes: meta.notes,
        createdAt: new Date().toISOString(),
      };
      const id = await saveBoilerECMResult(result);
      setResults(prev => [{ ...result, id }, ...prev]);
    } catch (err: unknown) {
      const e = err as { message?: string; code?: string };
      setError(`Failed to save: ${e?.message ?? e?.code ?? 'Unknown error'}`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBoilerECMResult(projectId, id);
      setResults(prev => prev.filter(r => r.id !== id));
    } catch { setError('Failed to delete result'); }
  };

  const activeECMs = [
    inputs.ecm1ReducedHoursPerDay > 0 && 'ECM #1',
    inputs.ecm2ImprovedEfficiencyPct > 0 && 'ECM #2',
    inputs.ecm3LoadReductionPct > 0 && 'ECM #3',
    inputs.ecm4ReducedDaysPerYear > 0 && 'ECM #4',
  ].filter(Boolean) as string[];

  // before/after bar lengths (relative to the larger of the two annual costs)
  const maxBar = Math.max(calc.annualCostAtMax, calc.ecmAnnualCostAtMax, 1);
  const curH = Math.max(8, Math.round((calc.annualCostAtMax / maxBar) * 100));
  const ecmH = Math.max(8, Math.round((calc.ecmAnnualCostAtMax / maxBar) * 100));
  const signColor = (n: number) => (n >= 0 ? token.colorSuccess : token.colorError);

  const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 };
  const field = (label: React.ReactNode, node: React.ReactNode) => (
    <div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      {node}
    </div>
  );

  const designTab = (
    <>
      <div style={grid2}>
        {field('Nameplate Input (MMBTUH)',
          <Input type="number" min={0} step={0.1} value={inputs.nameplateInputMmbtuh} onChange={setNum('nameplateInputMmbtuh')} />)}
        {field('Nameplate Output (MMBTUH)',
          <Input type="number" min={0} step={0.1} value={inputs.nameplateOutputMmbtuh} onChange={setNum('nameplateOutputMmbtuh')} />)}
        {field('Fuel Type',
          <Select style={{ width: '100%' }} value={meta.fuelType} options={FUEL_TYPES.map(toOption)} onChange={setMtVal('fuelType')} />)}
        {field('Service Type',
          <Select style={{ width: '100%' }} value={meta.serviceType} options={SERVICE_TYPES.map(toOption)} onChange={setMtVal('serviceType')} />)}
        {field('Flue Type',
          <Select style={{ width: '100%' }} value={meta.flueType} options={FLUE_TYPES.map(toOption)} onChange={setMtVal('flueType')} />)}
        {field('Construction',
          <Select style={{ width: '100%' }} value={meta.constructionType} options={CONSTRUCTION_TYPES.map(toOption)} onChange={setMtVal('constructionType')} />)}
      </div>
      <Row gutter={14} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card size="small"><Statistic title="Calc. max efficiency" value={fmtDec(calc.calculatedMaxEfficiencyPct, 1)} suffix="%" /></Card>
        </Col>
        <Col span={12}>
          <Card size="small"><Statistic title="Boiler horsepower" value={fmtDec(calc.calculatedHorsepower, 0)} suffix="BHP" /></Card>
        </Col>
      </Row>
      <Typography.Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 10 }}>
        Optional boiler details
      </Typography.Text>
      <div style={{ ...grid2, marginBottom: 0 }}>
        {([
          ['ID Number', 'idNumber'], ['Location', 'location'],
          ['Manufacturer', 'manufacturer'], ['Model Number', 'modelNumber'],
          ['Serial Number', 'serialNumber'], ['Nat. Board No.', 'nationalBoardNumber'],
          ['Year Built', 'yearBuilt'],
        ] as [string, keyof ReturnType<typeof defaultMeta>][]).map(([lbl, f]) => (
          <React.Fragment key={f}>
            {field(lbl, <Input value={meta[f] as string} onChange={setMt(f)} />)}
          </React.Fragment>
        ))}
      </div>
    </>
  );

  const operatingTab = (
    <>
      <div style={grid2}>
        {field('Operating Days/Year',
          <Input type="number" min={1} max={365} value={inputs.operatingDaysPerYear} onChange={setNum('operatingDaysPerYear')} />)}
        {field('Operating Hours/Day',
          <Input type="number" min={0} max={24} step={0.5} value={inputs.operatingHoursPerDay} onChange={setNum('operatingHoursPerDay')} />)}
        {field('Boiler Efficiency (%)',
          <Input type="number" min={1} max={100} step={0.1} value={inputs.boilerEfficiencyPct} onChange={setNum('boilerEfficiencyPct')} />)}
        {field('Fuel Cost ($/Therm)',
          <Input type="number" min={0} step={0.01} value={inputs.fuelCostPerTherm} onChange={setNum('fuelCostPerTherm')} />)}
        {field('Min Connected Load (MMBTUH)',
          <Input type="number" min={0} step={0.1} value={inputs.minConnectedLoadMmbtuh} onChange={setNum('minConnectedLoadMmbtuh')} />)}
        {field('Max Connected Load (MMBTUH)',
          <Input type="number" min={0} step={0.1} value={inputs.maxConnectedLoadMmbtuh} onChange={setNum('maxConnectedLoadMmbtuh')} />)}
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 2 }}>
          <span>Variable Firing Rate: <b>{inputs.variableFiringRatePct}%</b></span>
          <Typography.Text type="secondary">→ {fmtDec(calc.loadAtSelected, 2)} MMBTUH</Typography.Text>
        </div>
        <Slider min={0} max={100} step={1} value={inputs.variableFiringRatePct} onChange={setNumVal('variableFiringRatePct')} />
      </div>
      <Card size="small">
        <Statistic title="Run-time hours/year" value={calc.runTimeHours.toLocaleString()} suffix="hrs" />
      </Card>
    </>
  );

  const ecmTab = (
    <>
      {activeECMs.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          {activeECMs.map(e => <Tag key={e} color="green">{e} Active</Tag>)}
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        {field(<span>ECM #1 — Reduced Operating Hours {inputs.ecm1ReducedHoursPerDay > 0 && <Tag color="green" style={{ marginLeft: 6 }}>Active</Tag>}</span>, (
          <>
            <Select<number>
              style={{ width: '100%' }} value={inputs.ecm1ReducedHoursPerDay}
              options={ECM1_OPTIONS.map(v => ({ value: v, label: v === 0 ? '0 (no change)' : `Reduce by ${v} hr${v !== 1 ? 's' : ''}/day` }))}
              onChange={setNumVal('ecm1ReducedHoursPerDay')}
            />
            {inputs.ecm1ReducedHoursPerDay > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 5 }}>
                {inputs.operatingHoursPerDay} → {calc.ecmOperatingHoursPerDay} hrs/day
              </Typography.Text>
            )}
          </>
        ))}
      </div>

      <div style={{ marginBottom: 14 }}>
        {field(<span>ECM #2 — Improved Boiler Efficiency {inputs.ecm2ImprovedEfficiencyPct > 0 && <Tag color="green" style={{ marginLeft: 6 }}>Active</Tag>}</span>, (
          <>
            <Select<number>
              style={{ width: '100%' }} value={inputs.ecm2ImprovedEfficiencyPct}
              options={ECM2_OPTIONS.map(v => ({ value: v, label: v === 0 ? '0% (no change)' : `+${v}% improvement` }))}
              onChange={setNumVal('ecm2ImprovedEfficiencyPct')}
            />
            {inputs.ecm2ImprovedEfficiencyPct > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 5 }}>
                {inputs.boilerEfficiencyPct}% → {calc.ecmEfficiencyPct}%
              </Typography.Text>
            )}
          </>
        ))}
      </div>

      <div style={{ marginBottom: 14 }}>
        {field(<span>ECM #3 — Heating Load Reduction {inputs.ecm3LoadReductionPct > 0 && <Tag color="green" style={{ marginLeft: 6 }}>Active</Tag>}</span>, (
          <>
            <Select<number>
              style={{ width: '100%' }} value={inputs.ecm3LoadReductionPct}
              options={ECM3_OPTIONS.map(v => ({ value: v, label: v === 0 ? '0% (no change)' : `${v}% load reduction` }))}
              onChange={setNumVal('ecm3LoadReductionPct')}
            />
            {inputs.ecm3LoadReductionPct > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 5 }}>
                Max load: {inputs.maxConnectedLoadMmbtuh} → {fmtDec(calc.ecmMaxLoad, 2)} MMBTUH
              </Typography.Text>
            )}
          </>
        ))}
      </div>

      <div style={{ marginBottom: 14 }}>
        {field(<span>ECM #4 — Operating Day Reduction {inputs.ecm4ReducedDaysPerYear > 0 && <Tag color="green" style={{ marginLeft: 6 }}>Active</Tag>}</span>, (
          <>
            <Select<number>
              style={{ width: '100%' }} value={inputs.ecm4ReducedDaysPerYear}
              options={ECM4_OPTIONS.map(v => ({ value: v, label: v === 0 ? '0 (no change)' : `Reduce by ${v} day${v !== 1 ? 's' : ''}/year` }))}
              onChange={setNumVal('ecm4ReducedDaysPerYear')}
            />
            {inputs.ecm4ReducedDaysPerYear > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 5 }}>
                {inputs.operatingDaysPerYear} → {calc.ecmOperatingDays} days/year
              </Typography.Text>
            )}
          </>
        ))}
      </div>

      {field('Notes', (
        <Input.TextArea rows={2} value={meta.notes} onChange={e => setMeta(prev => ({ ...prev, notes: e.target.value }))} />
      ))}
    </>
  );

  const firingCols = (extraTitle: string, extraRender: (v: number, r: FiringRow) => React.ReactNode): ColumnsType<FiringRow> => [
    { title: 'Firing Rate', dataIndex: 'rate', render: (v, r) => (r.selected ? <b>{v}</b> : v) },
    { title: 'Annual Cost', dataIndex: 'annual', align: 'right', render: v => fmt$(v) },
    { title: extraTitle, dataIndex: 'extra', align: 'right', render: extraRender },
  ];

  const currentRows: FiringRow[] = [
    { key: 'min', rate: `Min (${fmtDec(inputs.minConnectedLoadMmbtuh, 1)} MMBTUH)`, annual: calc.annualCostAtMin, extra: calc.hourlyCostAtMin },
    { key: 'sel', rate: `Selected (${inputs.variableFiringRatePct}% → ${fmtDec(calc.loadAtSelected, 2)} MMBTUH)`, annual: calc.annualCostAtSelected, extra: calc.hourlyCostAtSelected, selected: true },
    { key: 'max', rate: `Max (${fmtDec(inputs.maxConnectedLoadMmbtuh, 1)} MMBTUH)`, annual: calc.annualCostAtMax, extra: calc.hourlyCostAtMax },
  ];

  const ecmRows: FiringRow[] = [
    { key: 'min', rate: `Min (${fmtDec(calc.ecmMinLoad, 2)} MMBTUH)`, annual: calc.ecmAnnualCostAtMin, extra: calc.savingsAtMin },
    { key: 'sel', rate: `Selected (${fmtDec(calc.ecmLoadAtSelected, 2)} MMBTUH)`, annual: calc.ecmAnnualCostAtSelected, extra: calc.savingsMidRange, selected: true },
    { key: 'max', rate: `Max (${fmtDec(calc.ecmMaxLoad, 2)} MMBTUH)`, annual: calc.ecmAnnualCostAtMax, extra: calc.savingsAtMax },
  ];

  const savedColumns: ColumnsType<BoilerECMResult> = [
    { title: 'Label', dataIndex: 'label', render: v => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Date', dataIndex: 'createdAt', width: 120, render: v => new Date(v).toLocaleDateString() },
    { title: 'Annual Cost (Max)', dataIndex: 'annualCostAtMax', align: 'right', render: v => fmt$(v) },
    {
      title: 'ECM Savings (Mid)', key: 'savings', align: 'right',
      render: (_, r) => activeEcmsOf(r)
        ? <Typography.Text style={{ color: token.colorSuccess }}>{fmt$(r.savingsMidRange)}</Typography.Text>
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Active ECMs', key: 'ecms', align: 'right',
      render: (_, r) => activeEcmsOf(r) || <Typography.Text type="secondary">None</Typography.Text>,
    },
    {
      title: '', key: 'actions', width: 50, align: 'right',
      render: (_, r) => (
        <Popconfirm title="Delete this ECM result?" onConfirm={() => handleDelete(r.id!)} okText="Delete" okButtonProps={{ danger: true }}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Delete result" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              <FireOutlined style={{ color: token.colorPrimary, marginRight: 8 }} />Boiler ECM
            </Typography.Title>
            <Typography.Text type="secondary">Energy conservation measure estimator</Typography.Text>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input placeholder="Boiler label (e.g. Boiler B-1)" value={meta.label} onChange={setMt('label')} style={{ width: 240 }} />
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save result</Button>
          </div>
        </div>
      </Card>

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} style={{ marginBottom: 16 }} />}

      <Row gutter={[16, 16]}>
        {/* ─── Left: Inputs ─── */}
        <Col xs={24} lg={10}>
          <Card styles={{ body: { paddingTop: 8 } }}>
            <Tabs
              activeKey={activeTab}
              onChange={k => setActiveTab(k as 'design' | 'operating' | 'ecm')}
              items={[
                { key: 'design', label: 'Boiler Design', children: designTab },
                { key: 'operating', label: 'Operating', children: operatingTab },
                { key: 'ecm', label: 'ECM Params', children: ecmTab },
              ]}
            />
          </Card>
        </Col>

        {/* ─── Right: Live Results ─── */}
        <Col xs={24} lg={14}>
          <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '20px 24px' } }}>
            {calc.anyECMActive ? (
              <>
                <Statistic
                  title="Projected annual savings"
                  value={fmt$(calc.savingsMidRange)}
                  suffix={<Typography.Text type="secondary" style={{ fontSize: 15 }}>/yr</Typography.Text>}
                  valueStyle={{ color: token.colorPrimary }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {fmtDec(calc.savingsPctAtMax, 1)}% reduction in fuel cost at max firing
                </Typography.Text>
              </>
            ) : (
              <>
                <Statistic
                  title="Current annual fuel cost (max load)"
                  value={fmt$(calc.annualCostAtMax)}
                  suffix={<Typography.Text type="secondary" style={{ fontSize: 15 }}>/yr</Typography.Text>}
                />
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Select an ECM parameter to project savings
                </Typography.Text>
              </>
            )}
          </Card>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            {(calc.anyECMActive ? [
              { title: 'Current cost', value: fmt$(calc.annualCostAtMax), suffix: undefined as string | undefined },
              { title: 'ECM cost', value: fmt$(calc.ecmAnnualCostAtMax), suffix: undefined },
              { title: 'ECM run-time', value: calc.ecmRunTimeHours.toLocaleString(), suffix: 'hrs' },
            ] : [
              { title: 'Max efficiency', value: fmtDec(calc.calculatedMaxEfficiencyPct, 1), suffix: '%' },
              { title: 'Boiler HP', value: fmtDec(calc.calculatedHorsepower, 0), suffix: 'BHP' },
              { title: 'Run-time/yr', value: calc.runTimeHours.toLocaleString(), suffix: 'hrs' },
            ]).map(k => (
              <Col flex="1 1 0" key={k.title}>
                <Card styles={{ body: { padding: '16px 20px' } }}>
                  <Statistic title={k.title} value={k.value} suffix={k.suffix} valueStyle={{ fontSize: 20 }} />
                </Card>
              </Col>
            ))}
          </Row>

          {calc.anyECMActive && (
            <Card
              title={<span><BarChartOutlined /> Annual fuel cost — current vs ECM</span>}
              style={{ marginBottom: 16 }}
            >
              {[
                { label: 'Current', pct: curH, value: calc.annualCostAtMax },
                { label: 'With ECM', pct: ecmH, value: calc.ecmAnnualCostAtMax },
              ].map(b => (
                <div key={b.label} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 110px', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                  <span style={{ fontSize: 13 }}>{b.label}</span>
                  <div style={{ height: 12, background: token.colorFillTertiary, borderRadius: 6, position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: 0, width: `${b.pct}%`, background: token.colorPrimary, borderRadius: '6px 4px 4px 6px' }} />
                  </div>
                  <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}><b>{fmt$(b.value)}</b></span>
                </div>
              ))}
              <Tag color="green">Saves {fmt$(calc.savingsAtMax)}/yr at max load</Tag>
            </Card>
          )}

          {/* Current Operations */}
          <Card
            title={<span><DashboardOutlined /> Current Operations</span>}
            extra={<Tag color="green">{calc.runTimeHours.toLocaleString()} hrs/yr</Tag>}
            style={{ marginBottom: 16 }}
            styles={{ body: { padding: 0 } }}
          >
            <Table<FiringRow>
              size="middle"
              pagination={false}
              columns={firingCols('Hourly Cost', v => `${fmt$(v)}/hr`)}
              dataSource={currentRows}
            />
          </Card>

          {/* ECM Applied */}
          {calc.anyECMActive ? (
            <Card
              title={<span><CheckCircleOutlined /> ECM Applied Results</span>}
              extra={<Tag color="green">{calc.ecmRunTimeHours.toLocaleString()} hrs/yr</Tag>}
              styles={{ body: { padding: 0 } }}
            >
              <Row gutter={16} style={{ padding: '16px 24px' }}>
                <Col flex="1 1 0"><Statistic title="Days/year" value={calc.ecmOperatingDays} valueStyle={{ fontSize: 20 }} /></Col>
                <Col flex="1 1 0"><Statistic title="Hours/day" value={fmtDec(calc.ecmOperatingHoursPerDay, 1)} valueStyle={{ fontSize: 20 }} /></Col>
                <Col flex="1 1 0"><Statistic title="Efficiency" value={fmtDec(calc.ecmEfficiencyPct, 1)} suffix="%" valueStyle={{ fontSize: 20 }} /></Col>
              </Row>
              <Table<FiringRow>
                size="middle"
                pagination={false}
                columns={firingCols('Savings', (v: number) => <span style={{ color: signColor(v) }}>{fmt$(v)}</span>)}
                dataSource={ecmRows}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', padding: '12px 24px' }}>
                <SafetyCertificateOutlined /> Calculated with Sustainability-Pros' proprietary boiler engine
              </Typography.Text>
            </Card>
          ) : (
            <Alert type="info" showIcon message="Select one or more ECM parameters to see projected savings." />
          )}
        </Col>
      </Row>

      {/* Saved Results */}
      <Card
        title={<span><HistoryOutlined /> Saved Results</span>}
        style={{ marginTop: 16 }}
        styles={{ body: { padding: 0 } }}
      >
        <Table<BoilerECMResult>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={savedColumns}
          dataSource={results}
          pagination={results.length > 20 ? { pageSize: 20 } : false}
          locale={{ emptyText: 'No saved results yet.' }}
        />
      </Card>
    </div>
  );
};

export default BoilerECMModule;
